import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { supabase } from "@/integrations/supabase/client";
import { useNotifications } from "@/hooks/useNotifications";
import { useCustomPasswordChange } from "@/hooks/useCustomPasswordChange";
import { useNavigate } from "react-router-dom";

export const PasswordChangeForm = ({ onCancel }: { onCancel: () => void }) => {
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [isRecovery, setIsRecovery] = useState(false);
  const { success, error: showError } = useNotifications();
  const { changePassword, isLoading: isCustomChanging } = useCustomPasswordChange();
  const navigate = useNavigate();

  // Check if this is a recovery password change
  useEffect(() => {
    const recoveryToken = localStorage.getItem('recovery_token');
    if (recoveryToken) {
      setIsRecovery(true);
    }
  }, []);

  const handlePasswordChange = async (e: React.FormEvent) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      showError("Las contraseñas no coinciden");
      return;
    }

    if (isRecovery) {
      // Use custom password change for recovery flow
      const result = await changePassword(newPassword);
      if (result.success) {
        setNewPassword("");
        setConfirmPassword("");
        onCancel();
        // Redirect to auth page for login
        navigate("/auth");
      }
    } else {
      // Use regular Supabase password change for authenticated users
      setIsChangingPassword(true);
      try {
        const { error } = await supabase.auth.updateUser({
          password: newPassword
        });

        if (error) throw error;

        success("Contraseña actualizada", {
          description: "Tu contraseña ha sido actualizada correctamente."
        });
        
        onCancel();
        setNewPassword("");
        setConfirmPassword("");
      } catch (error: any) {
        console.error("Password change error:", error);
        showError(error.message || "No se pudo actualizar la contraseña.");
      } finally {
        setIsChangingPassword(false);
      }
    }
  };

  return (
    <form onSubmit={handlePasswordChange} className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor="newPassword">Nueva contraseña</Label>
        <Input
          id="newPassword"
          type="password"
          value={newPassword}
          onChange={(e) => setNewPassword(e.target.value)}
          className="bg-background border-input focus:border-primary transition-colors"
          placeholder="••••••••"
          required
          minLength={6}
        />
      </div>
      <div className="space-y-2">
        <Label htmlFor="confirmPassword">Confirmar contraseña</Label>
        <Input
          id="confirmPassword"
          type="password"
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
          className="bg-background border-input focus:border-primary transition-colors"
          placeholder="••••••••"
          required
          minLength={6}
        />
      </div>
      <div className="flex gap-2">
        <Button
          type="submit"
          className="flex-1 bg-primary hover:bg-primary/90 transition-colors"
          disabled={isChangingPassword || isCustomChanging}
        >
          {(isChangingPassword || isCustomChanging) ? "Actualizando..." : "Actualizar contraseña"}
        </Button>
        <Button
          type="button"
          variant="outline"
          onClick={onCancel}
          className="flex-1"
        >
          Cancelar
        </Button>
      </div>
    </form>
  );
};